import { useEffect } from 'react';

import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { Book, Course, License, LicenseApi, Product } from 'apis/LicenseApi';
import { GMetrixStepper } from 'components/GMetrixStepper/GMetrixStepper';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { helpContent } from 'pages/licenses/content_management/help';
import { LicenseTable } from 'components/LicenseTable/LicenseTable';
import { NotificationContextProvider } from 'app/notifications';
import { FormattedMessage, useIntl } from 'react-intl';
import { PageHeader } from 'components/layout/page';
import { DefaultLayout } from 'components/layout';
import { KendoIntlProvider } from 'app/culture';
import { useNavigate } from 'react-router-dom';
import { GMetrixTable } from 'components';
import { Card } from 'react-bootstrap';
import { useSuperState } from 'hooks';

import 'pages/licenses/content_management/styles/LicenseContentManagement.scss';

type ContentItem = { id: number; text: string };

enum EStep {
    TYPE,
    ADD,
    INCLUDE,
    EXCLUDE,
    REVIEW
}

export const LicenseContentManagement = () => {

    const [state, setState] = useSuperState({
        contentData: [] as Product[] | Course[] | Book[],
        contentIds: [] as number[],
        dropDownData: [] as ContentItem[],
        itemsToAdd: [] as ContentItem[],
        itemsToExclude: [] as ContentItem[],
        licensesAddedTo: [] as ContentItem[],
        loadingMatches: false,
        numberOfLicenses: [] as License[],
        saving: false,
        selectedItems: [] as License[],
        showLicenseType: '',
        step: EStep.TYPE as EStep
    });
    const {
        contentData,
        contentIds,
        dropDownData,
        itemsToAdd,
        itemsToExclude,
        licensesAddedTo,
        loadingMatches,
        numberOfLicenses,
        saving,
        selectedItems,
        showLicenseType,
        step
    } = state;

    const { formatMessage, locale } = useIntl();
    const navigate = useNavigate();
    const buildMatchRequest = () => {

        const newContentIds: number[] = [];
        const includeIds: number[] = [];
        const excludeIds: number[] = [];

        contentData.forEach(item => {

            if (itemsToAdd.find(item2 => item2.id === item.Id)) newContentIds.push(item.Id);
            if (licensesAddedTo.find(item2 => item2.id === item.Id)) includeIds.push(item.Id);
            if (itemsToExclude.find(item2 => item2.id === item.Id)) excludeIds.push(item.Id);

        });

        return {
            excludeIds,
            includeIds,
            newContentIds
        };

    };

    const fetchMatchingLicenses = async () => {

        const { excludeIds, includeIds, newContentIds } = buildMatchRequest();

        setState({
            contentIds: newContentIds,
            loadingMatches: true,
            numberOfLicenses: [],
            selectedItems: []
        });

        try {

            const result = await LicenseApi.GetLicensesByIncludeExclude(
                showLicenseType,
                includeIds,
                excludeIds
            );

            setState({
                loadingMatches: false,
                numberOfLicenses: result,
                selectedItems: result
            });

        } catch {

            setState({ loadingMatches: false });

        }

    };

    const handleApply = async () => {

        setState({ saving: true });

        try {

            await LicenseApi.AddContentToLicenses(
                showLicenseType,
                contentIds,
                selectedItems.map(item => item.Id)
            );

            setState({ saving: false });
            window.location.reload();

        } catch {

            setState({ saving: false });

        }

    };

    const clearMatches = () => setState({
        contentIds: [],
        numberOfLicenses: [],
        selectedItems: []
    });

    const handleNext = async () => {

        if (step === EStep.TYPE && !showLicenseType) return;
        if (step === EStep.ADD && itemsToAdd.length === 0) return;
        if (step === EStep.INCLUDE && licensesAddedTo.length === 0) return;
        if (step >= EStep.REVIEW) return;

        const nextStep = step + 1;
        setState({ step: nextStep });

        if (nextStep === EStep.REVIEW) {

            await fetchMatchingLicenses();

        }

    };

    const handleBack = () => {

        if (step === EStep.TYPE) {

            navigate('/licenses/search');
            return;

        }

        setState({ step: step - 1 });

    };

    useEffect(() => {

        const loadContent = async () => {

            setState({
                contentData: [],
                dropDownData: [],
                itemsToAdd: [],
                itemsToExclude: [],
                licensesAddedTo: [],
                step: EStep.TYPE
            });
            clearMatches();

            if (!showLicenseType) return;

            try {

                let result: Product[] | Course[] | Book[];

                if (showLicenseType === 'Product') {

                    result = await LicenseApi.GetProduct();

                } else if (showLicenseType === 'Course') {

                    result = await LicenseApi.GetCourse();

                } else {

                    result = await LicenseApi.GetBook();

                }

                setState({
                    contentData: result,
                    dropDownData: result.map(r => ({ id: r.Id, text: r.Name }))
                });

            } catch {

                // silent - dropDownData stays empty

            }

        };

        loadContent();

    }, [showLicenseType]); // eslint-disable-line react-hooks/exhaustive-deps

    const isNextDisabled =
        (step === EStep.TYPE && !showLicenseType) ||
        (step === EStep.ADD && itemsToAdd.length === 0) ||
        (step === EStep.INCLUDE && licensesAddedTo.length === 0) ||
        step === EStep.REVIEW;

    return (
        <NotificationContextProvider>
            <KendoIntlProvider>
                <LocalizationProvider language={locale}>
                    <IntlProvider locale={locale}>
                        <DefaultLayout pageTitle={formatMessage({ id: 'licenses.11b1c36a', defaultMessage: 'Licenses' })}>

                            <div className="license-content-management d-flex flex-column gap-3" >

                                <PageHeader helpContent={helpContent} title={formatMessage({ id: 'add_content_to_licenses.e8ac1c84', defaultMessage: 'Add Content to Licenses' })}/>

                                <Card body className="flex-grow-1 d-flex flex-column">

                                    <GMetrixStepper
                                        linear
                                        stepItems={[
                                            { isValid: showLicenseType !== '' || step === EStep.TYPE, label: formatMessage({ id: 'select_type.3914aab2', defaultMessage: 'Select Type' }) },
                                            { label: formatMessage({ id: 'select_content.012bd0b4', defaultMessage: 'Select Content' }) },
                                            { label: formatMessage({ id: 'set_include_rules.e8f88808', defaultMessage: 'Set Include Rules' }) },
                                            { label: formatMessage({ id: 'set_exclude_rules.74b9246e', defaultMessage: 'Set Exclude Rules' }) },
                                            { label: formatMessage({ id: 'review_and_confirm.d9228900', defaultMessage: 'Review & Confirm' }) }
                                        ]}
                                        handleChange={e => setState({ step: e.value as EStep })}
                                        setStep={s => setState({ step: typeof s === 'function' ? s(step) as EStep : s as EStep })}
                                        step={step}
                                    />

                                    <div className="flex-grow-1 py-4">

                                        {step === EStep.TYPE && (
                                            <>
                                                <h5 className="mb-2">
                                                    <FormattedMessage defaultMessage="Step 1: Select Content Type" id="step_1_select_content_type.12f1cd71" />
                                                </h5>
                                                <p className="mb-3 text-muted">
                                                    <FormattedMessage defaultMessage="Choose what type of content you want to add to licenses." id="choose_what_type_of_content_you_want_to.bedc1550" />
                                                </p>
                                                <div className="d-flex gap-2 flex-wrap">
                                                    {[
                                                        { label: `Add ${formatMessage({ id: 'product.cf8158fa', defaultMessage: 'Product', description: 'Gridview header' })}`, type: 'Product' },
                                                        { label: `Add ${formatMessage({ id: 'course.790646f3', defaultMessage: 'Course' })}`, type: 'Course' },
                                                        { label: `Add ${formatMessage({ id: 'ebook.5d34c24a', defaultMessage: 'EBook' })}`, type: 'Book' }
                                                    ].map(option => (
                                                        <GMetrixButton
                                                            key={option.type}
                                                            onClick={() => setState({ showLicenseType: option.type })}
                                                            variant={showLicenseType === option.type ? 'primary' : 'outline-primary'}
                                                        >
                                                            {option.label}
                                                        </GMetrixButton>
                                                    ))}
                                                </div>
                                            </>
                                        )}

                                        {step === EStep.ADD && (
                                            <>
                                                <h5 className="mb-2">
                                                    <FormattedMessage defaultMessage="Step 2: Select Content To Add" id="step_2_select_content_to_add.81a351a1" />
                                                </h5>
                                                {dropDownData.length > 0 ? (
                                                    <LicenseTable
                                                        setSelectedData={items => {

                                                            setState({ itemsToAdd: items as ContentItem[] });
                                                            clearMatches();

                                                        }}
                                                        data={dropDownData}
                                                        header={formatMessage({ id: 'choose_types_to_add.a114c040', defaultMessage: 'Choose {type}(s) to add' }, { type: showLicenseType })}
                                                        itemType={showLicenseType}
                                                        label={formatMessage({ id: 'add_to_license.f1b22d25', defaultMessage: 'Add to License' })}
                                                    />
                                                ) : (
                                                    <p className="text-muted mb-0">
                                                        <FormattedMessage defaultMessage="Loading" id="loading.4959bc37" />
                                                        {' '}
                                                        {showLicenseType.toLowerCase()}
                                                        {' '}
                                                        <FormattedMessage defaultMessage="options..." id="options.557f8c29" />
                                                    </p>
                                                )}
                                            </>
                                        )}

                                        {step === EStep.INCLUDE && (
                                            <>
                                                <h5 className="mb-2">
                                                    <FormattedMessage defaultMessage="Step 3: Include Rules" id="step_3_include_rules.8511c770" />
                                                </h5>
                                                <p className="mb-3 text-muted">
                                                    <FormattedMessage defaultMessage="Select licenses that already contain these" id="select_licenses_that_already_contain_the.2b23227d" />
                                                    {' '}
                                                    {showLicenseType.toLowerCase()}
                                                    {' '}
                                                    <FormattedMessage defaultMessage="items." id="items.68327f11" />
                                                </p>
                                                {dropDownData.length > 0 ? (
                                                    <LicenseTable
                                                        setSelectedData={items => {

                                                            setState({ licensesAddedTo: items as ContentItem[] });
                                                            clearMatches();

                                                        }}
                                                        data={dropDownData}
                                                        header={formatMessage({ id: 'licenses_with_these_types.8ac768ef', defaultMessage: 'Licenses with these {type}(s)' }, { type: showLicenseType })}
                                                        itemType={showLicenseType}
                                                    />
                                                ) : (
                                                    <p className="text-muted mb-0">
                                                        <FormattedMessage defaultMessage="Loading" id="loading.4959bc37" />
                                                        {' '}
                                                        {showLicenseType.toLowerCase()}
                                                        {' '}
                                                        <FormattedMessage defaultMessage="options..." id="options.557f8c29" />
                                                    </p>
                                                )}
                                            </>
                                        )}

                                        {step === EStep.EXCLUDE && (
                                            <>
                                                <h5 className="mb-2">
                                                    <FormattedMessage defaultMessage="Step 4: Exclude Rules (Optional)" id="step_4_exclude_rules_optional.5cab3036" />
                                                </h5>
                                                <p className="mb-3 text-muted">
                                                    <FormattedMessage defaultMessage="Optionally exclude licenses that contain these" id="optionally_exclude_licenses_that_contain.377cb806" />
                                                    {' '}
                                                    {showLicenseType.toLowerCase()}
                                                    {' '}
                                                    <FormattedMessage defaultMessage="items." id="items.68327f11" />
                                                </p>
                                                {dropDownData.length > 0 ? (
                                                    <LicenseTable
                                                        setSelectedData={items => {

                                                            setState({ itemsToExclude: items as ContentItem[] });
                                                            clearMatches();

                                                        }}
                                                        data={[{ id: -1, text: formatMessage({ id: 'none.a51ad9ee', defaultMessage: 'None' }) }, ...dropDownData]}
                                                        header={formatMessage({ id: 'exclude_licenses_with_these_types.d083e6e7', defaultMessage: 'Exclude licenses with these {type}(s)' }, { type: showLicenseType })}
                                                        itemType={showLicenseType}
                                                    />
                                                ) : (
                                                    <p className="text-muted mb-0">
                                                        <FormattedMessage defaultMessage="Loading" id="loading.4959bc37" />
                                                        {' '}
                                                        {showLicenseType.toLowerCase()}
                                                        {' '}
                                                        <FormattedMessage defaultMessage="options..." id="options.557f8c29" />
                                                    </p>
                                                )}
                                            </>
                                        )}

                                        {step === EStep.REVIEW && (
                                            <>
                                                <h5 className="mb-2">
                                                    <FormattedMessage defaultMessage="Step 5: Review Matching Licenses & Confirm" id="step_5_review_matching_licenses_and_conf.be58e4d7" />
                                                </h5>
                                                <p className="mb-3 text-muted">
                                                    <FormattedMessage defaultMessage="Matching licenses were found from your selections. Deselect any license you do not want updated, then confirm to apply." id="matching_licenses_were_found_from_your_s.5f53087d" />
                                                </p>

                                                {loadingMatches ? (
                                                    <p className="text-muted mb-3">
                                                        <FormattedMessage defaultMessage="Finding matching licenses..." id="finding_matching_licenses.dc1836d2" />
                                                    </p>
                                                ) : (
                                                    <>
                                                        <GMetrixTable
                                                            selectableRows
                                                            bottomBarTableName={formatMessage({ id: 'licenses.400ffbf4', defaultMessage: 'License(s)' })}
                                                            columnData={[{ field: 'Id' }, { field: 'Name' }, { field: 'LicenseType' }]}
                                                            onSelectedChange={items => setState({ selectedItems: items as License[] })}
                                                            rowData={numberOfLicenses}
                                                            selected={selectedItems}
                                                        />
                                                        <div className="d-flex justify-content-end gap-2 mt-3">
                                                            <GMetrixButton
                                                                onClick={fetchMatchingLicenses}
                                                                variant="outline-primary"
                                                            >
                                                                <FormattedMessage defaultMessage="Refresh Matches" id="refresh_matches.20aa4523" />
                                                            </GMetrixButton>
                                                            <GMetrixButton
                                                                disabled={saving || numberOfLicenses.length === 0 || selectedItems.length === 0}
                                                                onClick={handleApply}
                                                                variant="primary"
                                                            >
                                                                {saving ? <FormattedMessage defaultMessage="Applying..." id="applying.7d6a7ba5" /> : <FormattedMessage defaultMessage="Confirm and Apply" id="confirm_and_apply.6bea8c25" />}
                                                            </GMetrixButton>
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )}

                                    </div>

                                    <div className="d-flex justify-content-between border-top pt-3 mt-auto">
                                        <GMetrixButton
                                            onClick={handleBack}
                                            variant="outline-secondary"
                                        >
                                            <FormattedMessage defaultMessage="Back" id="back.b6238c9e" />
                                        </GMetrixButton>
                                        <GMetrixButton
                                            disabled={isNextDisabled}
                                            onClick={handleNext}
                                            variant="primary"
                                        >
                                            <FormattedMessage defaultMessage="Next" id="next.50b7834e" />
                                        </GMetrixButton>
                                    </div>

                                </Card>

                            </div>

                        </DefaultLayout>
                    </IntlProvider>
                </LocalizationProvider>
            </KendoIntlProvider>
        </NotificationContextProvider>
    );

};